// Super-Admin screen for the messaging configuration (see
// `features/passwordless-login.md`): the ListMonk instances TAE sends
// transactional email through, and the per-language template ids the
// passwordless-login email resolves. Reached from the top-level app menu (the
// menu item is Super-Admin-only, and the server enforces the same on every
// request). Secret tokens never come back from the server — an instance only
// reports whether a token is stored — so editing keeps the token unless a new
// one is typed.

import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DnsIcon from '@mui/icons-material/Dns';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  DEFAULT_SUBSCRIBER_MODE,
  MESSAGE_PURPOSES,
  SUBSCRIBER_MODES,
  isHttpUrl,
  type LanguageCode,
  type ListMonkInstanceInput,
  type ListMonkInstanceView,
  type MessagePurpose,
  type MessageTemplateView,
  type SubscriberMode,
} from '../domain';
import {
  createListMonkInstance,
  deleteListMonkInstance,
  deleteMessageTemplate,
  fetchListMonkInstances,
  fetchMessageTemplates,
  saveMessageTemplate,
  updateListMonkInstance,
} from '../data/apiClient';
import { LOCALES, LOCALE_META } from '../i18n/messages';
import { useTranslation } from '../state/I18nContext';

export function MessagingView() {
  const { t } = useTranslation();

  const [instances, setInstances] = useState<ListMonkInstanceView[]>([]);
  const [templates, setTemplates] = useState<MessageTemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [instanceDialog, setInstanceDialog] = useState<{
    open: boolean;
    editing: ListMonkInstanceView | null;
  }>({ open: false, editing: null });
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [pendingInstance, setPendingInstance] = useState<ListMonkInstanceView | null>(null);
  const [pendingTemplate, setPendingTemplate] = useState<MessageTemplateView | null>(null);
  const [busy, setBusy] = useState(false);

  // Loaders read the translator through a ref so a language change never
  // refetches (mirrors UsersView).
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    try {
      const [nextInstances, nextTemplates] = await Promise.all([
        fetchListMonkInstances(),
        fetchMessageTemplates(),
      ]);
      setInstances(nextInstances);
      setTemplates(nextTemplates);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : tRef.current('admin.messaging.errors.load'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const instanceRegion = (id: number): string =>
    instances.find((i) => i.id === id)?.region ?? String(id);

  const confirmDeleteInstance = async () => {
    if (!pendingInstance) return;
    const target = pendingInstance;
    setPendingInstance(null);
    setBusy(true);
    setError(null);
    try {
      setInstances(await deleteListMonkInstance(target.id));
      // Templates cascade with the instance — refresh them too.
      setTemplates(await fetchMessageTemplates());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.messaging.errors.delete'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!pendingTemplate) return;
    const target = pendingTemplate;
    setPendingTemplate(null);
    setBusy(true);
    setError(null);
    try {
      setTemplates(await deleteMessageTemplate(target.id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.messaging.errors.delete'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.messaging.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.messaging.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={4}>
          {/* Instances */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t('admin.messaging.instances.title')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setInstanceDialog({ open: true, editing: null })}
              >
                {t('admin.messaging.instances.add')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('admin.messaging.instances.subtitle')}
            </Typography>
            {instances.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.messaging.instances.empty')}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {instances.map((instance) => (
                  <Paper
                    key={instance.id}
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <DnsIcon color="primary" />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" noWrap>
                          {instance.region}
                        </Typography>
                        {instance.isDefault && (
                          <Chip
                            size="small"
                            color="primary"
                            label={t('admin.messaging.instances.defaultChip')}
                          />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={instance.hasToken ? 'success' : 'warning'}
                          label={
                            instance.hasToken
                              ? t('admin.messaging.instances.tokenSet')
                              : t('admin.messaging.instances.tokenMissing')
                          }
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {instance.baseUrl} · {instance.username}
                      </Typography>
                    </Box>
                    <Tooltip title={t('admin.common.edit')}>
                      <span>
                        <IconButton
                          aria-label={t('admin.common.edit')}
                          disabled={busy}
                          onClick={() =>
                            setInstanceDialog({ open: true, editing: instance })
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('admin.common.delete')}>
                      <span>
                        <IconButton
                          aria-label={t('admin.common.delete')}
                          disabled={busy}
                          onClick={() => setPendingInstance(instance)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Templates */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t('admin.messaging.templates.title')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={instances.length === 0}
                onClick={() => setTemplateDialogOpen(true)}
              >
                {t('admin.messaging.templates.add')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('admin.messaging.templates.subtitle')}
            </Typography>
            {templates.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {instances.length === 0
                    ? t('admin.messaging.templates.dialog.noInstances')
                    : t('admin.messaging.templates.empty')}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {templates.map((template) => (
                  <Paper
                    key={template.id}
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <EmailOutlinedIcon color="primary" />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" noWrap>
                          {LOCALE_META[template.language].label}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t(`admin.messaging.purposes.${template.purpose}`)}
                        />
                        <Chip
                          size="small"
                          label={t('admin.messaging.templates.templateId', {
                            id: template.templateId,
                          })}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {instanceRegion(template.instanceId)}
                      </Typography>
                    </Box>
                    <Tooltip title={t('admin.common.delete')}>
                      <span>
                        <IconButton
                          aria-label={t('admin.common.delete')}
                          disabled={busy}
                          onClick={() => setPendingTemplate(template)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      <InstanceDialog
        open={instanceDialog.open}
        instance={instanceDialog.editing}
        onClose={() => setInstanceDialog({ open: false, editing: null })}
        onSaved={(next) => {
          setInstances(next);
          setInstanceDialog({ open: false, editing: null });
        }}
      />

      <TemplateDialog
        open={templateDialogOpen}
        instances={instances}
        onClose={() => setTemplateDialogOpen(false)}
        onSaved={(next) => {
          setTemplates(next);
          setTemplateDialogOpen(false);
        }}
      />

      <Dialog open={pendingInstance !== null} onClose={() => setPendingInstance(null)}>
        <DialogTitle>{t('admin.messaging.instances.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingInstance
              ? t('admin.messaging.instances.deleteConfirm', {
                  region: pendingInstance.region,
                })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingInstance(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmDeleteInstance()}>
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingTemplate !== null} onClose={() => setPendingTemplate(null)}>
        <DialogTitle>{t('admin.messaging.templates.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.messaging.templates.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTemplate(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmDeleteTemplate()}>
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

interface InstanceDialogProps {
  open: boolean;
  instance: ListMonkInstanceView | null;
  onClose: () => void;
  onSaved: (instances: ListMonkInstanceView[]) => void;
}

function InstanceDialog({ open, instance, onClose, onSaved }: InstanceDialogProps) {
  const { t } = useTranslation();
  const creating = instance === null;

  const [region, setRegion] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [subscriberMode, setSubscriberMode] = useState<SubscriberMode>(
    DEFAULT_SUBSCRIBER_MODE,
  );
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRegion(instance?.region ?? '');
    setBaseUrl(instance?.baseUrl ?? '');
    setUsername(instance?.username ?? '');
    setToken('');
    setSubscriberMode(instance?.subscriberMode ?? DEFAULT_SUBSCRIBER_MODE);
    setIsDefault(instance?.isDefault ?? false);
    setError(null);
  }, [open, instance]);

  const valid =
    region.trim().length > 0 &&
    isHttpUrl(baseUrl) &&
    username.trim().length > 0 &&
    // A token is required only when creating; editing keeps the stored one.
    (!creating || token.length > 0);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const input: ListMonkInstanceInput = {
      region,
      baseUrl,
      username,
      subscriberMode,
      isDefault,
      // Omit an empty token on edit so the server keeps the stored one.
      ...(token.length > 0 ? { token } : creating ? { token } : {}),
    };
    try {
      if (creating) {
        await createListMonkInstance(input);
      } else {
        await updateListMonkInstance(instance.id, input);
      }
      onSaved(await fetchListMonkInstances());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.messaging.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {creating
          ? t('admin.messaging.instances.dialog.addTitle')
          : t('admin.messaging.instances.dialog.editTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('admin.messaging.instances.dialog.region')}
            fullWidth
            autoFocus={creating}
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            helperText={t('admin.messaging.instances.dialog.regionHelp')}
          />
          <TextField
            label={t('admin.messaging.instances.dialog.baseUrl')}
            fullWidth
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://listmonk.example.com"
          />
          <TextField
            label={t('admin.messaging.instances.dialog.username')}
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label={t('admin.messaging.instances.dialog.token')}
            type="password"
            fullWidth
            value={token}
            onChange={(e) => setToken(e.target.value)}
            helperText={creating ? undefined : t('admin.messaging.instances.dialog.tokenKeep')}
          />
          <FormControl fullWidth>
            <InputLabel id="instance-subscriber-mode-label">
              {t('admin.messaging.instances.dialog.subscriberMode')}
            </InputLabel>
            <Select
              labelId="instance-subscriber-mode-label"
              label={t('admin.messaging.instances.dialog.subscriberMode')}
              value={subscriberMode}
              onChange={(e) => setSubscriberMode(e.target.value as SubscriberMode)}
            >
              {SUBSCRIBER_MODES.map((m) => (
                <MenuItem key={m} value={m}>
                  {t(`admin.messaging.subscriberModes.${m}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {t('admin.messaging.instances.dialog.subscriberModeHelp')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
            }
            label={t('admin.messaging.instances.dialog.isDefault')}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('admin.common.cancel')}</Button>
        <Button variant="contained" disabled={!valid || busy} onClick={() => void submit()}>
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface TemplateDialogProps {
  open: boolean;
  instances: ListMonkInstanceView[];
  onClose: () => void;
  onSaved: (templates: MessageTemplateView[]) => void;
}

function TemplateDialog({ open, instances, onClose, onSaved }: TemplateDialogProps) {
  const { t } = useTranslation();

  const [instanceId, setInstanceId] = useState<number | ''>('');
  const [purpose, setPurpose] = useState<MessagePurpose>(MESSAGE_PURPOSES[0]);
  const [language, setLanguage] = useState<LanguageCode>(LOCALES[0]);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInstanceId(instances.find((i) => i.isDefault)?.id ?? instances[0]?.id ?? '');
    setPurpose(MESSAGE_PURPOSES[0]);
    setLanguage(LOCALES[0]);
    setTemplateId('');
    setError(null);
  }, [open, instances]);

  const parsedId = Number(templateId);
  const valid =
    typeof instanceId === 'number' && Number.isInteger(parsedId) && parsedId > 0;

  const submit = async () => {
    if (!valid || busy || typeof instanceId !== 'number') return;
    setBusy(true);
    setError(null);
    try {
      const next = await saveMessageTemplate({
        instanceId,
        purpose,
        language,
        templateId: parsedId,
      });
      onSaved(next);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.messaging.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('admin.messaging.templates.dialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="template-instance-label">
              {t('admin.messaging.templates.dialog.instance')}
            </InputLabel>
            <Select
              labelId="template-instance-label"
              label={t('admin.messaging.templates.dialog.instance')}
              value={instanceId}
              onChange={(e) => setInstanceId(e.target.value as number)}
            >
              {instances.map((instance) => (
                <MenuItem key={instance.id} value={instance.id}>
                  {instance.region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="template-purpose-label">
              {t('admin.messaging.templates.dialog.purpose')}
            </InputLabel>
            <Select
              labelId="template-purpose-label"
              label={t('admin.messaging.templates.dialog.purpose')}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as MessagePurpose)}
            >
              {MESSAGE_PURPOSES.map((code) => (
                <MenuItem key={code} value={code}>
                  {t(`admin.messaging.purposes.${code}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="template-language-label">
              {t('admin.messaging.templates.dialog.language')}
            </InputLabel>
            <Select
              labelId="template-language-label"
              label={t('admin.messaging.templates.dialog.language')}
              value={language}
              onChange={(e) => setLanguage(e.target.value as LanguageCode)}
            >
              {LOCALES.map((code) => (
                <MenuItem key={code} value={code}>
                  {LOCALE_META[code].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('admin.messaging.templates.dialog.templateId')}
            type="number"
            fullWidth
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            helperText={t('admin.messaging.templates.dialog.templateIdHelp')}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('admin.common.cancel')}</Button>
        <Button variant="contained" disabled={!valid || busy} onClick={() => void submit()}>
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
